import type {
    RestaurantOperationZoneDetailDto,
    RestaurantOperationZoneLegalAddressDto,
} from '@/services/innovorder/operationZone/operationZone.type';

const COUNTRIES_WITHOUT_TAX_NUMBER: ReadonlySet<string> = new Set(['MA']);

export const isZoneFullyConfigured = (zone: RestaurantOperationZoneDetailDto): boolean => {
    const hasRequiredTaxNumber =
        COUNTRIES_WITHOUT_TAX_NUMBER.has(zone.operationZone.countryCode) || Boolean(zone.legal?.taxNumber);
    return Boolean(
        zone.legal?.socialReason &&
            hasRequiredTaxNumber &&
            (zone.legal?.siren || zone.legal?.fiscalCode) &&
            zone.legal?.address?.postalCode &&
            zone.legal?.address?.locality,
    );
};

export const formatLegalAddressName = (address: RestaurantOperationZoneLegalAddressDto | null | undefined): string => {
    if (!address) return '';
    const street = [address.streetNumber, address.route].filter(Boolean).join(' ');
    return [street, address.postalCode, address.locality, address.country].filter(Boolean).join(', ');
};

export type Territory = {
    id: string;
    name: string;
    code: string;
    flag: string;
    solution: string;
    availableProviders: string[];
    countryCode: string;
    currencies: string[];
};

export type FiscalFieldName = 'vatNumber' | 'siret' | 'fiscalRepresentative';

export type FiscalFieldLabels = {
    taxLabel: string;
    taxPlaceholder: string;
    taxFieldName: FiscalFieldName;
    secondLabel: string;
    secondPlaceholder: string;
    secondFieldName: FiscalFieldName;
};

export const FISCAL_FIELDS_BY_COUNTRY: Record<string, FiscalFieldLabels> = {
    FR: {
        taxLabel: 'operationZones.configModal.vatNumber',
        taxPlaceholder: 'operationZones.configModal.vatNumber.placeholder',
        taxFieldName: 'vatNumber',
        secondLabel: 'operationZones.configModal.siret',
        secondPlaceholder: 'operationZones.configModal.siret.placeholder',
        secondFieldName: 'siret',
    },
    ES: {
        taxLabel: 'operationZones.configModal.nifCif',
        taxPlaceholder: 'operationZones.configModal.nifCif.placeholder',
        taxFieldName: 'vatNumber',
        secondLabel: 'operationZones.configModal.fiscalRepresentative',
        secondPlaceholder: 'operationZones.configModal.fiscalRepresentative.placeholder',
        secondFieldName: 'fiscalRepresentative',
    },
    IT: {
        taxLabel: 'operationZones.configModal.vatNumber',
        taxPlaceholder: 'operationZones.configModal.vatNumber.placeholder',
        taxFieldName: 'vatNumber',
        secondLabel: 'operationZones.configModal.fiscalIdCode',
        secondPlaceholder: 'operationZones.configModal.fiscalIdCode.placeholder',
        secondFieldName: 'siret',
    },
    BE: {
        taxLabel: 'operationZones.configModal.vatNumberBtw',
        taxPlaceholder: 'operationZones.configModal.vatNumberBtw.placeholder',
        taxFieldName: 'vatNumber',
        secondLabel: 'operationZones.configModal.enterpriseNumber',
        secondPlaceholder: 'operationZones.configModal.enterpriseNumber.placeholder',
        secondFieldName: 'siret',
    },
    MA: {
        taxLabel: 'operationZones.configModal.if',
        taxPlaceholder: 'operationZones.configModal.if.placeholder',
        taxFieldName: 'fiscalRepresentative',
        secondLabel: 'operationZones.configModal.ice',
        secondPlaceholder: 'operationZones.configModal.ice.placeholder',
        secondFieldName: 'siret',
    },
};

export const COUNTRY_FLAG_MAP: Record<string, string> = {
    FR: '\u{1F1EB}\u{1F1F7}',
    IT: '\u{1F1EE}\u{1F1F9}',
    ES: '\u{1F1EA}\u{1F1F8}',
    LU: '\u{1F1F1}\u{1F1FA}',
    BE: '\u{1F1E7}\u{1F1EA}',
    DE: '\u{1F1E9}\u{1F1EA}',
    PT: '\u{1F1F5}\u{1F1F9}',
    NL: '\u{1F1F3}\u{1F1F1}',
    GB: '\u{1F1EC}\u{1F1E7}',
    CH: '\u{1F1E8}\u{1F1ED}',
    MA: '\u{1F1F2}\u{1F1E6}',
};
